import { useCallback } from 'react';
import {
  ScratchpadRecommendation,
  ScratchpadRecommendationRequestType,
} from '../../../../../../../api/graphql/generated';
import { getLocalizedText } from '../../../../../../../i18n/i18n.utils';
import { logger } from '../../../../../../../util/logger';
import { ParametersRecommendationInfo } from '../../../../../shared/component/flow-item-parameter/parameter-list';
import { useCreateRecommendationForForm } from '../../../../../shared/component/settings/recommendation.hook';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';
import {
  FlowItemForm,
  FlowItemParameter,
  SystemUnit,
} from '../../../../../shared/model/flow.types';
import { FlowItemService } from '../../../../../shared/service/service.types';
import { convertParameterValueToUOM } from '../../../../../shared/util/flow-parameter.util';
import { BeaconCuttingData } from '../../../model/beacon-cutting-data';

export type CutParameterRecommendation = FlowItemParameter & {
  // NOTE cutting params don't use min and max
  minValue: number | undefined;
  maxValue: number | undefined;
};

export interface CutSettingsRecommendation {
  parameters: CutParameterRecommendation[];
}

export type SingleCutRecommendation = Omit<
  ScratchpadRecommendation,
  'outputs'
> & {
  recommendation: CutSettingsRecommendation;
};

type CreateCuttingRecommendationForFormType = (
  form: FlowItemForm,
  tags: Record<string, unknown>
) => Promise<SingleCutRecommendation | undefined>;

const useCreateCutSettingsRecommendationForForm =
  (): CreateCuttingRecommendationForFormType => {
    const createRecommendation =
      useCreateRecommendationForForm<BeaconCuttingData.FlowConfig>({
        appSlug: BeaconCuttingData.APP_SLUG,
        recommendationType: ScratchpadRecommendationRequestType.SETTINGS,
      });

    const { flowItemService, flowService, flow, system } =
      useFlowServices<BeaconCuttingData.FlowConfig>();

    const createCutSettingsRecommendationForForm = useCallback(
      async (
        form: FlowItemForm,
        tags: Record<string, unknown>
      ): Promise<SingleCutRecommendation | undefined> => {
        let flowItem = flowItemService.convertFormToFlowItem(form, system);

        // remove the params to be predicted
        BeaconCuttingData.RECOMMENDED_PARAM_OUTPUTS.forEach((paramRef) => {
          flowItem = flowItemService.setParameterValue(
            flowItem,
            paramRef,
            undefined
          );
        });

        const { config } =
          flowService.mapApplicationDefaultParameterSetTagsToFlowConfig(
            flow,
            tags
          );

        const recommendation = await createRecommendation(flowItem, config);

        if (!recommendation) {
          return;
        }

        // NOTE in the welding we send back an array
        const filteredRecommendation: CutSettingsRecommendation =
          recommendation.outputs.recommendation;

        if (filteredRecommendation === undefined) {
          logger.error('failed to get the correct recommendation', {
            recommendation,
          });
          return;
        }

        const parameters = convertCutApplicationParameterValuesToUOM(
          flowItemService,
          filteredRecommendation.parameters,
          system
        );

        const { outputs: _outputs, ...rest } = recommendation;

        return {
          ...rest,
          recommendation: {
            ...filteredRecommendation,
            parameters,
          },
        };
      },
      [flow, flowItemService, flowService, createRecommendation, system]
    );

    return createCutSettingsRecommendationForForm;
  };

const setFormParamValues = (
  parameters: FlowItemParameter[],
  setFieldValue: (
    field: string,
    value: unknown,
    shouldValidate?: boolean | undefined
  ) => void,
  formikValuePath: string
) => {
  // set recommended values and labels
  for (const param of parameters ?? []) {
    const value = param.value;
    const fieldPath = `${formikValuePath}.parameters.${param.ref}.value`;

    setFieldValue(fieldPath, value);
  }
};

const createParamSubtitlesForSettingsRecommendations = (
  parameters: CutParameterRecommendation[]
): ParametersRecommendationInfo => {
  const recommendationSubtitles: ParametersRecommendationInfo = {};

  // set recommended values and labels
  for (const param of parameters) {
    recommendationSubtitles[param.ref] = {
      subtext: createParamSubtitle(param),
    };
  }

  return recommendationSubtitles;
};

const createParamSubtitle = (
  paramRecommendation: CutParameterRecommendation
): string | undefined => {
  const min = paramRecommendation.minValue;
  const max = paramRecommendation.maxValue;
  const recommendedText =
    getLocalizedText('common:recommended') ?? 'Recommended';

  if (min !== undefined && max !== undefined) {
    return `${recommendedText}: ${min} - ${max}`;
  } else if (max !== undefined) {
    return `${recommendedText}: < ${max}`;
  } else if (min !== undefined) {
    return `${recommendedText}: > ${min}`;
  } else {
    return undefined;
  }
};

const convertCutApplicationParameterValuesToUOM = <FlowConfig,>(
  flowItemService: FlowItemService<FlowConfig>,
  parameters: CutParameterRecommendation[],
  system: SystemUnit
): CutParameterRecommendation[] => {
  return parameters
    .filter((param) => {
      // Exclude empty recommendations, such as when the api
      // sometimes can predict a param (e.g. travel speed) but for
      // this instance it didn't calculate a value.
      // This can happen when the inputs don't meet criteria.
      // This check avoids the UI trying to update form fields with nothing.
      return param.ref !== undefined && param.value !== undefined;
    })
    .map((param) => {
      const metadata = flowItemService.getParameterMetadata(
        BeaconCuttingData.FlowItemRef.CUT,
        param.ref
      );

      if (metadata && metadata.value.uom && metadata.value.type === 'number') {
        const value = convertParameterValueToUOM(
          metadata.value,
          system,
          param.value,
          false
        );

        const minValue = param.minValue
          ? convertParameterValueToUOM(
              metadata.value,
              system,
              param.minValue,
              false
            )
          : undefined;

        const maxValue = param.maxValue
          ? convertParameterValueToUOM(
              metadata.value,
              system,
              param.maxValue,
              false
            )
          : undefined;

        return {
          ...param,
          value,
          minValue,
          maxValue,
        };
      }

      return param;
    });
};

export {
  createParamSubtitlesForSettingsRecommendations,
  setFormParamValues,
  useCreateCutSettingsRecommendationForForm,
};
